import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import BrandMark from '../components/BrandMark';
import './Landing.css';
import { formatMoney2, parseStrikeAmount } from '../utils/planPrice';
import {
  FAQ_DATA,
  FALLBACK_PLANS,
  SOLUTION_FEATURE_ITEMS,
  USE_CASE_CARDS,
  landingFeaturesFromPlan,
} from '../data/marketingData';
import { useMarketingPlans } from '../hooks/useMarketingPlans';

const heroBadgeCheck = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="3" aria-hidden>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const painPointIcons = [
  (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#06B6D4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
      <line x1="2" y1="20" x2="8" y2="14" />
      <line x1="22" y1="20" x2="16" y2="14" />
    </svg>
  ),
  (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
];

const solutionIcons = [
  (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" aria-hidden>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#06B6D4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  ),
];

const useCaseIcons = [
  (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </svg>
  ),
  (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#06B6D4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M18 8h1a4 4 0 0 1 0 8h-1" />
      <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z" />
      <line x1="6" y1="1" x2="6" y2="4" />
      <line x1="10" y1="1" x2="10" y2="4" />
      <line x1="14" y1="1" x2="14" y2="4" />
    </svg>
  ),
  (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  (
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    </svg>
  ),
];

export default function Landing() {
  const { lang, toggleLang, t } = useLang();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const apiPlans = useMarketingPlans();
  const [mobileMenu, setMobileMenu] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="landing">
      {/* Floating particles background */}
      <div className="particles">
        {[...Array(20)].map((_, i) => (
          <span key={i} className="particle" style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 8}s`,
            animationDuration: `${6 + Math.random() * 10}s`,
            width: `${2 + Math.random() * 4}px`,
            height: `${2 + Math.random() * 4}px`,
          }} />
        ))}
      </div>

      {/* ── HEADER ── */}
      <header className={`nav ${scrolled ? 'nav--scrolled' : ''}`}>
        <div className="nav__inner">
          <div className="nav__logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <BrandMark
              imgClassName="nav__logo-icon nav__logo-icon--mark"
              fallbackClassName="nav__logo-icon"
            />
            <span>ReplyTek</span>
          </div>
          <nav className="nav__links">
            <a href="#features">{t('features')}</a>
            <a href="#pricing">{t('pricing')}</a>
            <a href="#faq">{t('faq')}</a>
          </nav>
          <button type="button" className="nav__hamburger" onClick={() => setMobileMenu(!mobileMenu)} aria-expanded={mobileMenu} aria-label="Menu">
            <span />
            <span />
            <span />
          </button>
          <div className="nav__actions">
            <button type="button" className="btn-lang" onClick={toggleLang}>{lang === 'en' ? 'ES' : 'EN'}</button>
            <button type="button" className="btn-ghost" onClick={() => navigate('/login')}>{t('sign_in')}</button>
            <button type="button" className="btn-primary" onClick={() => navigate('/signup')}>{t('get_started')}</button>
          </div>
        </div>
        {mobileMenu && (
          <div className="nav__mobile-menu">
            <a href="#features" onClick={() => setMobileMenu(false)}>{t('features')}</a>
            <a href="#pricing" onClick={() => setMobileMenu(false)}>{t('pricing')}</a>
            <a href="#faq" onClick={() => setMobileMenu(false)}>{t('faq')}</a>
            <div className="nav__mobile-actions">
              <button type="button" onClick={() => { setMobileMenu(false); navigate('/login'); }}>{t('sign_in')}</button>
              <button type="button" onClick={() => { setMobileMenu(false); navigate('/signup'); }} className="nav__mobile-cta">{t('get_started')}</button>
            </div>
          </div>
        )}
      </header>

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero__content">
          <h1 className="hero__title">{t('hero_title')}</h1>
          <p className="hero__subtitle">{t('hero_subtitle')}</p>
          <button type="button" className="btn-primary btn-lg" onClick={() => navigate('/signup')}>{t('hero_cta')}</button>
          <div className="hero__badges">
            <span className="badge"><span className="badge__check">{heroBadgeCheck}</span> {t('hero_badge1')}</span>
            <span className="badge"><span className="badge__check">{heroBadgeCheck}</span> {t('hero_badge2')}</span>
            <span className="badge"><span className="badge__check">{heroBadgeCheck}</span> {t('hero_badge3')}</span>
          </div>
        </div>
        <div className="hero__visual">
          <img src="/hero-ai.png" alt="AI Assistant responding to WhatsApp messages" className="hero__image" />
          <div className="hero__image-glow" aria-hidden />
        </div>
      </section>

      {/* ── PAIN POINTS ── */}
      <section className="section pain-points" id="features">
        <h2 className="section__title">
          {lang === 'en' ? 'Are you losing sales on WhatsApp every day?' : '¿Estás perdiendo ventas en WhatsApp cada día?'}
        </h2>
        <div className="cards-3">
          {[
            {
              title: { en: 'Slow Responses', es: 'Respuestas Lentas' },
              desc: { en: 'Customers leave when they don\'t get instant answers. Every minute of delay is a lost sale.', es: 'Los clientes se van cuando no reciben respuesta inmediata. Cada minuto de retraso es una venta perdida.' },
            },
            {
              title: { en: 'Lost Messages', es: 'Mensajes Perdidos' },
              desc: { en: 'Messages pile up while you sleep, eat, or handle other tasks. Potential orders disappear.', es: 'Los mensajes se acumulan mientras duermes, comes o haces otras tareas. Los pedidos potenciales desaparecen.' },
            },
            {
              title: { en: 'Manual Work', es: 'Trabajo Manual' },
              desc: { en: 'Answering the same questions over and over wastes hours of your day, every single day.', es: 'Responder las mismas preguntas una y otra vez desperdicia horas de tu día, todos los días.' },
            },
          ].map((card, i) => (
            <div className="pain-card" key={i}>
              <div className="pain-card__icon">{painPointIcons[i]}</div>
              <h3>{card.title[lang]}</h3>
              <p>{card.desc[lang]}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── SOLUTION ── */}
      <section className="section solution">
        <h2 className="section__title">
          {lang === 'en'
            ? 'What if every WhatsApp message got an instant answer?'
            : '¿Y si cada mensaje de WhatsApp tuviera respuesta al instante?'}
        </h2>
        <p className="section__subtitle">
          {lang === 'en'
            ? 'ReplyTek responds instantly to customer questions using your business information — even while you sleep.'
            : 'ReplyTek responde al instante a las preguntas de tus clientes usando la información de tu negocio — incluso mientras duermes.'}
        </p>
        <div className="solution__features">
          {SOLUTION_FEATURE_ITEMS.map((f, i) => (
            <div className="solution__item" key={i}>
              <span className="solution__icon">{solutionIcons[i]}</span>
              <span>{f.text[lang]}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── USE CASES ── */}
      <section className="section use-cases">
        <h2 className="section__title">
          {lang === 'en' ? 'Built for your business' : 'Hecho para tu negocio'}
        </h2>
        <div className="cards-4">
          {USE_CASE_CARDS.map((c, i) => (
            <div className="use-card usecase-card" key={i}>
              <div className="usecase-card__icon">{useCaseIcons[i]}</div>
              <h3>{c.title[lang]}</h3>
              <p>{c.desc[lang]}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 3 STEPS ── */}
      <section className="section steps">
        <h2 className="section__title">
          {lang === 'en' ? 'Get started in 3 simple steps' : 'Empieza en 3 simples pasos'}
        </h2>
        <div className="steps-container">
          <div className="steps__grid">
          {[
            { num: '1', title: { en: 'Connect WhatsApp', es: 'Conecta WhatsApp' }, desc: { en: 'Scan a QR code with your phone. Takes 10 seconds.', es: 'Escanea un código QR con tu teléfono. Toma 10 segundos.' } },
            { num: '2', title: { en: 'Set up your AI', es: 'Configura tu IA' }, desc: { en: 'Enter your business name, products, and hours. No coding needed.', es: 'Ingresa el nombre de tu negocio, productos y horarios. Sin programación.' } },
            {
              num: '3',
              title: { en: 'Let it answer 24/7', es: 'Déjala responder 24/7' },
              desc: {
                en: 'Your assistant replies to common questions automatically using the details you provided.',
                es: 'Tu asistente responde preguntas frecuentes automáticamente con los datos que configuraste.',
              },
            },
          ].map((s, i) => (
            <div className="step-card" key={i}>
              <div className="step-card__number">{s.num}</div>
              <h3>{s.title[lang]}</h3>
              <p>{s.desc[lang]}</p>
            </div>
          ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="section testimonials">
        <h2 className="section__title">
          {lang === 'en' ? 'Trusted by businesses like yours' : 'Negocios como el tuyo ya confían en nosotros'}
        </h2>
        <div className="cards-3">
          {[
            {
              name: 'María G.',
              role: { en: 'Online Store Owner', es: 'Dueña de Tienda Online' },
              quote: {
                en: 'I used to reply to the same product questions all day. Now the AI answers instantly and I get hours back every week.',
                es: 'Respondía las mismas preguntas de productos todo el día. Ahora la IA contesta al instante y recupero horas cada semana.',
              },
            },
            {
              name: 'Carlos R.',
              role: { en: 'Restaurant Manager', es: 'Gerente de Restaurante' },
              quote: {
                en: 'After hours we were missing messages. Customers still get our hours, menu info, and quick answers without us glued to the phone.',
                es: 'Fuera de horario se nos escapaban mensajes. Ahora los clientes reciben horarios, info del menú y respuestas rápidas sin estar pegados al teléfono.',
              },
            },
            {
              name: 'Ana L.',
              role: { en: 'Beauty Salon Owner', es: 'Dueña de Salón de Belleza' },
              quote: {
                en: 'Setup took a few minutes. People get instant answers about services and pricing — I spend less time typing and more time with clients in the salon.',
                es: 'La configuración tomó unos minutos. La gente recibe respuestas al instante sobre servicios y precios — escribo menos y atiendo más en el salón.',
              },
            },
          ].map((item, i) => (
            <div className="testimonial-card" key={i}>
              <p className="testimonial-card__quote">{item.quote[lang]}</p>
              <div className="testimonial-card__author">
                <div className="testimonial-card__avatar">{item.name[0]}</div>
                <div>
                  <strong>{item.name}</strong>
                  <span>{item.role[lang]}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── PRICING ── */}
      <section className="section pricing" id="pricing">
        <h2 className="section__title">
          {lang === 'en' ? 'Simple, transparent pricing' : 'Precios simples y transparentes'}
        </h2>
        <p className="section__subtitle">
          {lang === 'en' ? 'No credit card required · Cancel anytime' : 'No requiere tarjeta de crédito · Cancela cuando quieras'}
        </p>
        <div className="cards-3">
          {apiPlans && apiPlans.length > 0
            ? apiPlans.map((plan, i) => {
                const featured = apiPlans.length >= 2 ? i === 1 : i === 0;
                const feats = landingFeaturesFromPlan(plan, lang);
                return (
                  <div className={`price-card ${featured ? 'price-card--featured plan-card--popular' : ''}`} key={plan.id ?? i}>
                    {featured && (
                      <div className="price-card__badge">
                        {lang === 'en' ? 'Most Popular' : 'Más Popular'}
                      </div>
                    )}
                    <h3 className="price-card__name">{plan.title || '—'}</h3>
                    <div className="price-card__price">
                      {parseStrikeAmount(plan.price_strike) != null && (
                        <div className="plan-price-strike">
                          ${formatMoney2(parseStrikeAmount(plan.price_strike))}
                        </div>
                      )}
                      <div className="price-card__price-row">
                        <span className="price-card__currency">$</span>
                        <span className="price-card__amount">{formatMoney2(plan.price ?? 0)}</span>
                        <span className="price-card__period">/{lang === 'en' ? 'mo' : 'mes'}</span>
                      </div>
                    </div>
                    <ul className="price-card__features">
                      {feats.map((f, j) => (
                        <li key={j}>
                          <span className="check">✓</span> {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      className={`btn-${featured ? 'primary' : 'ghost'} btn-full`}
                      onClick={() => navigate('/signup')}
                    >
                      {t('get_started')}
                    </button>
                  </div>
                );
              })
            : FALLBACK_PLANS.map((plan, i) => (
                <div className={`price-card ${plan.highlight ? 'price-card--featured plan-card--popular' : ''}`} key={i}>
                  {plan.highlight && (
                    <div className="price-card__badge">{lang === 'en' ? 'Most Popular' : 'Más Popular'}</div>
                  )}
                  <h3 className="price-card__name">{plan.name[lang]}</h3>
                  <div className="price-card__price">
                    <div className="price-card__price-row">
                      <span className="price-card__currency">$</span>
                      <span className="price-card__amount">{formatMoney2(plan.price)}</span>
                      <span className="price-card__period">/{lang === 'en' ? 'mo' : 'mes'}</span>
                    </div>
                  </div>
                  <ul className="price-card__features">
                    {plan.features[lang].map((f, j) => (
                      <li key={j}>
                        <span className="check">✓</span> {f}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    className={`btn-${plan.highlight ? 'primary' : 'ghost'} btn-full`}
                    onClick={() => navigate('/signup')}
                  >
                    {t('get_started')}
                  </button>
                </div>
              ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="section faq" id="faq">
        <h2 className="section__title">
          {lang === 'en' ? 'Frequently asked questions' : 'Preguntas frecuentes'}
        </h2>
        <div className="faq__list">
          {FAQ_DATA.map((item, i) => (
            <div className={`faq__item ${openFaq === i ? 'faq__item--open' : ''}`} key={i} onClick={() => setOpenFaq(openFaq === i ? null : i)}>
              <div className="faq__question">
                <span>{item.q[lang]}</span>
                <span className="faq__chevron">{openFaq === i ? '−' : '+'}</span>
              </div>
              {openFaq === i && <div className="faq__answer">{item.a[lang]}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="section cta-final cta-section">
        <h2>
          {lang === 'en'
            ? 'Turn WhatsApp into your 24/7 customer service assistant'
            : 'Convierte WhatsApp en tu asistente de atención al cliente 24/7'}
        </h2>
        <button type="button" className="btn-primary btn-lg" onClick={() => navigate('/signup')}>{t('hero_cta')}</button>
      </section>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="footer__inner">
          <div className="footer__brand">
            <BrandMark
              imgClassName="nav__logo-icon nav__logo-icon--mark"
              fallbackClassName="nav__logo-icon"
            />
            <span>ReplyTek</span>
          </div>
          <div className="footer__links">
            <a href="#features">{t('features')}</a>
            <a href="#pricing">{t('pricing')}</a>
            <a href="#faq">{t('faq')}</a>
          </div>
          <p className="footer__disclaimer">
            {lang === 'en'
              ? 'ReplyTek is not affiliated with WhatsApp or Meta. WhatsApp is a trademark of Meta Platforms, Inc.'
              : 'ReplyTek no está afiliado con WhatsApp o Meta. WhatsApp es una marca registrada de Meta Platforms, Inc.'}
          </p>
          <p className="footer__copy">© 2026 ReplyTek. {lang === 'en' ? 'All rights reserved.' : 'Todos los derechos reservados.'}</p>
        </div>
      </footer>
    </div>
  );
}
